import _extends from "@babel/runtime-corejs3/helpers/extends";
import _defineProperty from "@babel/runtime-corejs3/helpers/defineProperty";
import _slicedToArray from "@babel/runtime-corejs3/helpers/slicedToArray";
import _concatInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/concat";
import React, { isValidElement, useContext, useImperativeHandle } from 'react';
import classNames from 'classnames';
import ConfigContext from '../config-provider/ConfigContext';
import { getCompProps } from '../_utils';
import ResizeObserver from 'resize-observer-polyfill';
import devWarning from '../_utils/devwarning';
import { tuple } from '../_utils/type';
import isBoolean from 'lodash/isBoolean';
import { FadeList } from './fadeList';
import { SlideList } from './slideList';
import { DisplayList } from './displayList';
import { Slidebar } from './slidebar';
import Icon from '../icon';
export var DotPositionTypes = tuple('top', 'left', 'bottom', 'right');
export var EffectTypes = tuple('scrollx', 'fade');

var InternalCarousel = function InternalCarousel(props, ref) {
  var _useContext = useContext(ConfigContext),
      getPrefixCls = _useContext.getPrefixCls,
      prefixCls = _useContext.prefixCls,
      userDefaultProps = _useContext.compDefaultProps;

  var carouselProps = getCompProps('Carousel', userDefaultProps, props);
  var autoplay = carouselProps.autoplay,
      jumpNode = carouselProps.jumpNode,
      dotPosition = carouselProps.dotPosition,
      children = carouselProps.children,
      dots = carouselProps.dots,
      easing = carouselProps.easing,
      effect = carouselProps.effect,
      intervalTime = carouselProps.intervalTime,
      afterChange = carouselProps.afterChange,
      beforeChange = carouselProps.beforeChange,
      customPrefixcls = carouselProps.prefixCls,
      className = carouselProps.className,
      style = carouselProps.style;
  devWarning(DotPositionTypes.indexOf(dotPosition) === -1, 'carousel', "cannot found dotPosition type '".concat(dotPosition, "'"));
  devWarning(EffectTypes.indexOf(effect) === -1, 'carousel', "cannot found effect type '".concat(effect, "'"));
  var carouselPrefixCls = getPrefixCls(prefixCls, 'carousel', customPrefixcls); // 按钮样式前缀

  var _React$useState = React.useState(0),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      itemWidth = _React$useState2[0],
      setItemWidth = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      needAnimation = _React$useState4[0],
      setNeedAnimation = _React$useState4[1];

  var _React$useState5 = React.useState(0),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      currentIndex = _React$useState6[0],
      setCurrentIndex = _React$useState6[1];

  var carouselRef = React.useRef();
  var listRef = React.useRef();
  var autoplayRef = React.useRef();
  var isScrollxEffect = React.useMemo(function () {
    return effect === 'scrollx';
  }, [effect]);
  var isFadeEffect = React.useMemo(function () {
    return effect === 'fade';
  }, [effect]);
  var isNoneEffect = React.useMemo(function () {
    return effect === 'none';
  }, [effect]);
  var posX = React.useMemo(function () {
    return -1 * (currentIndex + 1) * itemWidth;
  }, [currentIndex, itemWidth]);
  var reSize = React.useCallback(function (rect) {
    if (itemWidth !== rect.width && !rect.hide) {
      setItemWidth(rect.width);
    }
  }, [itemWidth]);
  var setScrollXEffectStyle = React.useCallback(function () {
    var _context;

    if (!listRef.current) return;
    listRef.current.style.cssText = needAnimation ? _concatInstanceProperty(_context = "transform: translateX(".concat(posX, "px); transition:all 0.3s ")).call(_context, easing) : "transform: translateX(".concat(posX, "px); transition:'none'");
  }, [needAnimation, easing, posX]);
  var jumpTo = React.useCallback(function (index, needAnimation) {
    if (isFadeEffect) {
      if (index === -1 || index === children.length) index = 0;
      beforeChange && beforeChange(currentIndex, index);
      setCurrentIndex(index);
      setNeedAnimation(needAnimation);
    } else if (index >= -1 && index <= children.length) {
      beforeChange && beforeChange(currentIndex, index);
      setCurrentIndex(index);
      setNeedAnimation(needAnimation);
    }
  }, [isFadeEffect, beforeChange, currentIndex, children === null || children === void 0 ? void 0 : children.length]);
  var next = React.useCallback(function () {
    jumpTo(currentIndex + 1, true);
  }, [currentIndex, jumpTo]);
  var prev = React.useCallback(function () {
    jumpTo(currentIndex - 1, true);
  }, [currentIndex, jumpTo]);
  var play = React.useCallback(function () {
    autoplayRef.current && clearTimeout(autoplayRef.current);
    autoplayRef.current = setTimeout(function () {
      next();
    }, intervalTime);
  }, [intervalTime, next]);
  React.useEffect(function () {
    var element = carouselRef.current;
    var handler = reSize;

    if (!element) {
      devWarning(!element && element !== null, 'useResizeMeasure', 'useResizeMeasure指定的元素不存在');
      return;
    }

    var measure = function measure(entries) {
      if (!entries[0] || !entries[0].contentRect) {
        return;
      }

      var contentRect = entries[0].contentRect;
      var hide = contentRect.width === 0 && contentRect.height === 0; // 隐藏条件：高宽都为0

      var bottom = contentRect.bottom,
          height = contentRect.height,
          left = contentRect.left,
          right = contentRect.right,
          top = contentRect.top,
          width = contentRect.width,
          x = contentRect.x,
          y = contentRect.y;
      handler && handler({
        bottom: bottom,
        height: height,
        left: left,
        right: right,
        top: top,
        width: width,
        x: x,
        y: y,
        hide: hide
      });
    };

    var resizeObserver = new ResizeObserver(measure);
    resizeObserver && resizeObserver.observe(element);
    return function () {
      resizeObserver && resizeObserver.disconnect();
    };
  }, [reSize]);
  React.useLayoutEffect(function () {
    isScrollxEffect && setScrollXEffectStyle();
  }, [setScrollXEffectStyle, isScrollxEffect]);
  React.useEffect(function () {
    if (autoplay) {
      play();
    }

    return function () {
      autoplayRef.current && clearTimeout(autoplayRef.current);
    };
  }, [currentIndex, autoplay, intervalTime, play]);

  var showDot = function showDot() {
    return isBoolean(dots) ? dots : true;
  };

  useImperativeHandle(ref, function () {
    return {
      next: next,
      prev: prev,
      jumpTo: jumpTo,
      getRef: function getRef() {
        return carouselRef.current;
      }
    };
  });
  var handleClick = React.useCallback(function (index) {
    beforeChange && beforeChange(currentIndex, index);
    setCurrentIndex(index);
    setNeedAnimation(true);
  }, [beforeChange, currentIndex]);
  var handleTransitionEnd = React.useCallback(function () {
    if (!autoplay || !(children === null || children === void 0 ? void 0 : children.length)) return;
    var childrenL = children.length;
    var newCurrentIndex = currentIndex;

    if (isScrollxEffect) {
      if (currentIndex === -1) {
        newCurrentIndex = childrenL - 1;
      }

      if (currentIndex === childrenL) {
        newCurrentIndex = 0;
      }
    }

    afterChange && afterChange(currentIndex);
    setCurrentIndex(newCurrentIndex);
    isScrollxEffect && setNeedAnimation(false);
  }, [currentIndex, children === null || children === void 0 ? void 0 : children.length, afterChange, isScrollxEffect, autoplay]);
  var handleMouseEnter = React.useCallback(function () {
    autoplayRef.current && clearTimeout(autoplayRef.current);
  }, []);
  var handleMouseLeave = React.useCallback(function () {
    autoplay && play();
  }, [play, autoplay]);
  var rootClassName = classNames(className, _defineProperty({}, "".concat(carouselPrefixCls, "-root"), true));

  var renderDisplayList = function renderDisplayList() {
    var content;

    if (isFadeEffect) {
      content = /*#__PURE__*/React.createElement(FadeList, {
        items: children,
        parentPrefixCls: carouselPrefixCls,
        needAnimation: needAnimation,
        currentIndex: currentIndex,
        ref: listRef,
        itemWidth: itemWidth
      });
    } else if (isNoneEffect) {
      content = /*#__PURE__*/React.createElement(DisplayList, {
        items: children,
        parentPrefixCls: carouselPrefixCls,
        currentIndex: currentIndex,
        ref: listRef
      });
    } else {
      content = /*#__PURE__*/React.createElement(SlideList, {
        items: children,
        parentPrefixCls: carouselPrefixCls,
        ref: listRef
      });
    }

    return content;
  };

  var renderJumpNode = function renderJumpNode() {
    if ((children === null || children === void 0 ? void 0 : children.length) && jumpNode) {
      var jumpClassPrefix = "".concat(carouselPrefixCls, "-jump");

      var leftClick = function leftClick(e) {
        e.stopPropagation();

        if (currentIndex !== 0) {
          prev();
        }
      };

      var rightClick = function rightClick(e) {
        e.stopPropagation();

        if (currentIndex !== children.length - 1) {
          next();
        }
      };

      var mergeNode = function mergeNode(node, type) {
        var onClick = function onClick(evt) {
          var chClick = node.props.onClick;

          if (type === 'left') {
            leftClick(evt);
          } else {
            rightClick(evt);
          }

          if (typeof chClick === 'function') {
            chClick(evt);
          }
        };

        return _extends(_extends({}, node.props), {
          onClick: onClick
        });
      };

      var leftNode = /*#__PURE__*/React.createElement("div", {
        className: classNames(jumpClassPrefix, "".concat(jumpClassPrefix, "-left"), _defineProperty({}, "".concat(jumpClassPrefix, "-disabled"), currentIndex <= 0))
      }, Array.isArray(jumpNode) && jumpNode.length > 0 && /*#__PURE__*/isValidElement(jumpNode[0]) ? /*#__PURE__*/React.cloneElement(jumpNode[0], mergeNode(jumpNode[0], 'left')) : /*#__PURE__*/React.createElement(Icon, {
        className: "".concat(jumpClassPrefix, "-icon"),
        type: 'arrow-left-circle-solid',
        onClick: leftClick
      }));
      var rightNode = /*#__PURE__*/React.createElement("div", {
        className: classNames(jumpClassPrefix, "".concat(jumpClassPrefix, "-right"), _defineProperty({}, "".concat(jumpClassPrefix, "-disabled"), currentIndex >= children.length - 1))
      }, Array.isArray(jumpNode) && jumpNode.length > 1 && /*#__PURE__*/isValidElement(jumpNode[1]) ? /*#__PURE__*/React.cloneElement(jumpNode[1], mergeNode(jumpNode[1], 'right')) : /*#__PURE__*/React.createElement(Icon, {
        className: "".concat(jumpClassPrefix, "-icon"),
        type: 'arrow-right-circle-solid',
        onClick: rightClick
      }));
      return /*#__PURE__*/React.createElement(React.Fragment, null, leftNode, rightNode);
    }

    return null;
  };

  return /*#__PURE__*/React.createElement("div", {
    className: rootClassName,
    ref: carouselRef,
    onTransitionEnd: function onTransitionEnd() {
      return handleTransitionEnd();
    },
    style: style,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave
  }, (children === null || children === void 0 ? void 0 : children.length) ? renderDisplayList() : null, (children === null || children === void 0 ? void 0 : children.length) && showDot() ? /*#__PURE__*/React.createElement(Slidebar, {
    number: children.length,
    currentIndex: currentIndex,
    dotsClassName: dots,
    parentPrefixCls: carouselPrefixCls,
    dotPosition: dotPosition,
    onClick: handleClick
  }) : null, renderJumpNode());
};

var Carousel = /*#__PURE__*/React.forwardRef(InternalCarousel);
Carousel.displayName = 'Carousel';
export default Carousel;